import { fromJsonArray } from 'src/core/utils';
import CalculatedVolumeDiscount from 'src/entities/cart/response/CalculatedVolumeDiscount';
import OrderProduct from 'src/entities/cart/response/OrderProduct';
import VolumeDiscount from 'src/entities/volume-discount/VolumeDiscount';

export default class OrderProductListGrouped {
    localizedName: string;
    normalizedName: string;
    discount: number;
    discountNet?: number;
    discountGross?: number;
    totalPrice: number;
    products: OrderProduct[];
    currencyIso: string;

    constructor(json: any) {
        this.localizedName = json.localizedName;
        this.normalizedName = json.normalizedName;
        this.discount = json.discount;
        this.discountNet = json.discountNet;
        this.discountGross = json.discountGross;
        this.totalPrice = json.totalPrice;
        this.products = fromJsonArray(OrderProduct, json.products);
        this.currencyIso = this.products[0]?.currencyIso ?? '';
    }

    /** The group's volume discount (a negative figure) in the requested mode. */
    public discountIn(gross: boolean): number {
        const exact = gross ? this.discountGross : this.discountNet;
        return exact ?? this.discount;
    }

    public get articleGroupNormalizedName(): string {
        return this.products[0] ? this.products[0].articleGroupNormalizedName : '';
    }

    public containsUnfinishedProductCustomFields(): boolean {
        return this.products.some(product => product.hasUnfinishedCustomFields());
    }

    /**
     * Calculates the total quantity of products within this group
     */
    public getTotalQuantity(targetUnit: string = ''): number {
        return this.products.reduce((total: number, product: OrderProduct) => {
            return getTotalQuantity(product.quantity, product.colliEntity, product.colli, targetUnit) + total;
        }, 0);
    }

    /**
     * Returns a calculated volume discount if this article group support it
     */
    public getCalculatedVolumeDiscount(volumeDiscounts: VolumeDiscount[]): CalculatedVolumeDiscount | undefined {
        // check if there is a potential volume discount available for this article group
        const matchingVolumeDiscount = volumeDiscounts.find(
            volumeDiscount => volumeDiscount.articleGroupNormalizedName === this.articleGroupNormalizedName
        );

        if (!matchingVolumeDiscount) return undefined;

        return new CalculatedVolumeDiscount(
            matchingVolumeDiscount,
            this.getTotalQuantity(matchingVolumeDiscount.baseColli),
            this.totalPrice,
            this.currencyIso
        );
    }
}

const UNIT_FACTORS = {
    g: { kg: 1000 },
    kg: { g: 0.001 },
};

const WEIGHT_UNITS = ['g', 'kg'];

const normalizeUnit = (unit: string): string => (unit ?? '').trim().replace(/\.$/, '').toLowerCase();

export const getBaseUnitFactor = (unit: string) => getUnitFactor('g', unit);

/**
 * Conversion factor from the product's unit into the rebate's unit. A weight tier (kg/g) counts
 * ONLY weight-based lines — a piece or package never stands in for a kilogram (3 × „Stk." is not
 * 3 kg), and the other way round.
 */
export const getUnitFactor = (targetUnit: string, unit: string): number => {
    const target = normalizeUnit(targetUnit);
    const source = normalizeUnit(unit);
    if (!target || target === source) return 1;
    const targetIsWeight = WEIGHT_UNITS.indexOf(target) !== -1;
    const sourceIsWeight = WEIGHT_UNITS.indexOf(source) !== -1;
    if (targetIsWeight !== sourceIsWeight) return 0;
    const factors = UNIT_FACTORS[target] ?? {};
    return factors[source] ?? 1;
};

export const getTotalQuantity = (
    quantity: number,
    colliEntity: string,
    colli: number,
    targetUnit: string = ''
): number => {
    const uf = getUnitFactor(targetUnit, colliEntity);
    return quantity * colli * uf;
};
